"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FederalOnlyRules = exports.Federal = exports.FEDERAL_THRESHOLDS = exports.FEDERAL_READABLE_GENERATOR_STATUSES = void 0;
exports.federalSource = federalSource;
const browser_1 = require("encamp-prisma/generated/browser");
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
const wasteGeneratorStatusThresholds_1 = require("encamp-shared/dist/constants/wasteGeneratorStatusThresholds");
const isNonEmpty_1 = require("encamp-shared/dist/utils/isNonEmpty");
const types_1 = require("../types");
const _base_1 = require("./_base");
/**
 * The federal generator statuses as the threshold result names them. A state
 * whose status IS the federal one reuses these; a state with a status name of
 * its own (Connecticut's CESQG) authors its own entry beside them.
 */
exports.FEDERAL_READABLE_GENERATOR_STATUSES = {
    LQG: { abbreviation: "LQG", name: "Large Quantity Generator" },
    SQG: { abbreviation: "SQG", name: "Small Quantity Generator" },
    VSQG: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
};
// ── Federal rules as thresholds ─────────────────────────────────────────────
/**
 * Every threshold federal law sets. A state rule that descends from one
 * spreads it under the state's citation; only a state-only rule gets its own.
 */
exports.FEDERAL_THRESHOLDS = {
    centralTimeLimit: {
        id: "centralTimeLimit",
        scope: "CONTAINER",
        kind: "DAYS_SINCE",
        name: "Central accumulation deadline",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 262.16, 262.17" },
        consequences: [{ kind: "COMPLIANCE_NOT_RECORDED", actionToComply: "SHIP" }],
        from: "CENTRAL_ARRIVAL",
        warnDays: types_1.APPROACHING_WINDOW_DAYS,
    },
    /**
     * The one-year limit 273.15(a) and 273.35(a) set word for word for small-
     * and large-quantity handlers. Handler category isn't modeled, so both are
     * cited.
     */
    universalWasteTimeLimit: {
        id: "universalWasteTimeLimit",
        scope: "CONTAINER",
        kind: "DAYS_SINCE",
        name: "Universal waste deadline",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 261.9, 273.1(b), 273.15(a), 273.35(a)" },
        basis: { container: { wasteKind: "UNIVERSAL_WASTE" } },
        consequences: [{ kind: "COMPLIANCE_NOT_RECORDED", actionToComply: "SHIP" }],
        from: "ACCUMULATION_START",
        value: 365,
        warnDays: types_1.APPROACHING_WINDOW_DAYS,
    },
    /**
     * The healthcare-facility limit for non-creditable pharmaceuticals. Creditable
     * ones have no clock, but nothing distinguishes them, so every Subpart P
     * container takes the stricter reading.
     */
    subpartPTimeLimit: {
        id: "subpartPTimeLimit",
        scope: "CONTAINER",
        kind: "DAYS_SINCE",
        name: "Pharmaceutical waste deadline",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 266.501(d), 266.502(f)(1)" },
        basis: { container: { wasteKind: "PHARMACEUTICAL" } },
        consequences: [{ kind: "COMPLIANCE_NOT_RECORDED", actionToComply: "SHIP" }],
        from: "ACCUMULATION_START",
        value: 365,
        warnDays: types_1.APPROACHING_WINDOW_DAYS,
    },
    /**
     * 261.6(a)(4) takes used oil managed under part 279 out of part 262's
     * clocks, and part 279 sets no generator storage time limit of its own.
     */
    usedOilTimeLimit: {
        id: "usedOilTimeLimit",
        scope: "CONTAINER",
        kind: "DAYS_SINCE",
        name: "Used oil deadline",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 261.6(a)(4), 279.22" },
        basis: { container: { wasteKind: "USED_OIL" } },
        consequences: [{ kind: "COMPLIANCE_NOT_RECORDED", actionToComply: "SHIP" }],
        from: "ACCUMULATION_START",
    },
    capNonAcuteVolume: {
        id: "capNonAcuteVolume",
        scope: "AREA",
        kind: "WASTE_AMOUNT",
        name: "Non-acute satellite limit",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 262.15" },
        consequences: [{ kind: "ACTIVATES_SECONDARY", thresholdIds: ["satelliteToCentralMoveWindow"] }],
        unit: "GALLONS",
        value: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
        warnPercent: wasteAccumulation_1.SAA_APPROACHING_RATIO * 100,
        includedCategories: ["NON_ACUTE"],
        includedPrograms: ["RCRA"],
    },
    capAcuteVolume: {
        id: "capAcuteVolume",
        scope: "AREA",
        kind: "WASTE_AMOUNT",
        name: "Acute satellite limit",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 262.15" },
        consequences: [{ kind: "ACTIVATES_SECONDARY", thresholdIds: ["satelliteToCentralMoveWindow"] }],
        unit: "GALLONS",
        value: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
        warnPercent: wasteAccumulation_1.SAA_APPROACHING_RATIO * 100,
        includedCategories: ["ACUTE"],
        includedPrograms: ["RCRA"],
    },
    capAcuteMass: {
        id: "capAcuteMass",
        scope: "AREA",
        kind: "WASTE_AMOUNT",
        name: "Acute satellite limit",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 262.15" },
        consequences: [{ kind: "ACTIVATES_SECONDARY", thresholdIds: ["satelliteToCentralMoveWindow"] }],
        unit: "KILOGRAMS",
        value: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
        warnPercent: wasteAccumulation_1.SAA_APPROACHING_RATIO * 100,
        includedCategories: ["ACUTE"],
        includedPrograms: ["RCRA"],
    },
    satelliteToCentralMoveWindow: {
        id: "satelliteToCentralMoveWindow",
        scope: "CONTAINER",
        kind: "DAYS_SINCE",
        name: "Satellite move window",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 262.15" },
        consequences: [{ kind: "COMPLIANCE_NOT_RECORDED", actionToComply: "MOVE_TO_CENTRAL" }],
        from: { binds: "ONLY_WHILE_CROSSED" },
        value: wasteAccumulation_1.SATELLITE_MOVE_WINDOW_DAYS,
        warnDays: "ALWAYS",
    },
    onSiteTotal: {
        id: "onSiteTotal",
        scope: "FACILITY",
        kind: "WASTE_AMOUNT",
        name: "On-site threshold",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 262.16(b)(1)" },
        // Known gap: over the cap, the site is a storage facility without a permit
        // exemption (262.10(g)(2)); nothing records or surfaces that yet.
        consequences: [{ kind: "COMPLIANCE_NOT_RECORDED", actionToComply: "SHIP" }],
        unit: "KILOGRAMS",
        warnPercent: wasteAccumulation_1.SAA_APPROACHING_RATIO * 100,
        includedCategories: ["ACUTE", "NON_ACUTE"],
        includedPrograms: ["RCRA"],
    },
    onSiteNonAcute: {
        id: "onSiteNonAcute",
        scope: "FACILITY",
        kind: "WASTE_AMOUNT",
        name: "Non-acute on-site threshold",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 262.14(a)(4)" },
        // Known gap: crossing puts that waste under 262.16's conditions on a
        // 180/270-day clock from the crossing; the generator's status is unchanged.
        consequences: [],
        unit: "KILOGRAMS",
        warnPercent: wasteAccumulation_1.SAA_APPROACHING_RATIO * 100,
        includedCategories: ["NON_ACUTE"],
        includedPrograms: ["RCRA"],
    },
    onSiteAcute: {
        id: "onSiteAcute",
        scope: "FACILITY",
        kind: "WASTE_AMOUNT",
        name: "Acute on-site threshold",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 262.14(a)(3)" },
        // Known gap: crossing puts that waste under 262.17's conditions on a
        // 90-day clock from the crossing; the generator's status is unchanged.
        consequences: [],
        unit: "KILOGRAMS",
        warnPercent: wasteAccumulation_1.SAA_APPROACHING_RATIO * 100,
        includedCategories: ["ACUTE"],
        includedPrograms: ["RCRA"],
    },
    episodicWindow: {
        id: "episodicWindow",
        scope: "CONTAINER",
        kind: "DAYS_SINCE",
        name: "Episodic event deadline",
        source: { jurisdiction: "FEDERAL", citation: "40 CFR 262 subpart L" },
        consequences: [{ kind: "COMPLIANCE_NOT_RECORDED", actionToComply: "SHIP" }],
        from: "EPISODIC_START",
        value: wasteAccumulation_1.EPISODIC_WINDOW_DAYS,
        warnDays: types_1.APPROACHING_WINDOW_DAYS,
    },
};
function federalSource(citation) {
    return { jurisdiction: "FEDERAL", citation };
}
/**
 * Which program governs a container here. A universal-waste category counts
 * only where the state's program covers it (40 CFR 273.9, 262.13(c)(6)), and
 * pharmaceutical waste only at a facility operating under Subpart P
 * (266.501); otherwise both are ordinary waste.
 */
function governingProgram(container) {
    switch (container.wasteKind) {
        case undefined:
            return { missing: ["container.wasteKind"] };
        case "UNIVERSAL_WASTE":
            if (container.managedAsUniversalWaste === undefined) {
                return { missing: ["container.managedAsUniversalWaste"] };
            }
            return { value: container.managedAsUniversalWaste ? "UNIVERSAL_WASTE" : "RCRA" };
        case "USED_OIL":
            return { value: "USED_OIL" };
        case "PHARMACEUTICAL":
            if (container.operatesUnderSubpartP === undefined) {
                return { missing: ["container.operatesUnderSubpartP"] };
            }
            return { value: container.operatesUnderSubpartP ? "PHARMACEUTICAL" : "RCRA" };
        case "RCRA":
        case null:
            return { value: "RCRA" };
    }
}
/**
 * Which side of the acute / non-acute split federal law puts a container's
 * waste on. STATE_REGULATED waste is outside federal hazardous waste.
 */
function category(container) {
    switch (container.classification) {
        case undefined:
            return { missing: ["container.classification"] };
        case browser_1.WasteClassification.RCRA_ACUTELY_HAZARDOUS:
            return { value: "ACUTE" };
        case browser_1.WasteClassification.STATE_REGULATED:
        case browser_1.WasteClassification.NONHAZARDOUS:
            return { value: "EXCLUDED" };
        case browser_1.WasteClassification.RCRA_HAZARDOUS:
        case null:
            // An unclassified container counts non-acute: acute would trip the
            // 1-quart limit on every drum awaiting characterization.
            return { value: "NON_ACUTE" };
    }
}
/** The part 262 central clock when a fact that picks among 262.14, 262.16, and 262.17 is missing. */
function undeterminedCentralTimeLimit(missing) {
    return {
        ...exports.FEDERAL_THRESHOLDS.centralTimeLimit,
        source: federalSource("40 CFR 262.14, 262.16, 262.17"),
        determination: "UNDETERMINED",
        missing,
        basis: {},
    };
}
/**
 * The part 262 central clock for ordinary waste, by federal generator status.
 * The 90/180/270-day limits run from arrival in central storage.
 */
function centralTimeLimit(situation) {
    const status = situation.facility.federalGeneratorStatus;
    switch (status) {
        case undefined:
            return undeterminedCentralTimeLimit(["facility.federalGeneratorStatus"]);
        case "VSQG":
            // 262.14 imposes no time limit; a VSQG is constrained by its on-site
            // quantity thresholds instead.
            return {
                ...exports.FEDERAL_THRESHOLDS.centralTimeLimit,
                source: federalSource("40 CFR 262.14"),
                determination: "EXEMPT",
                basis: { facility: { federalGeneratorStatus: status } },
            };
        case "LQG":
            return {
                ...exports.FEDERAL_THRESHOLDS.centralTimeLimit,
                source: federalSource("40 CFR 262.17(a)"),
                determination: "KNOWN",
                basis: { facility: { federalGeneratorStatus: status } },
                value: 90,
            };
        case "SQG": {
            const { mustTransport200Miles } = situation.container;
            if (mustTransport200Miles === undefined) {
                return {
                    ...exports.FEDERAL_THRESHOLDS.centralTimeLimit,
                    source: federalSource("40 CFR 262.16(b), (c)"),
                    determination: "UNDETERMINED",
                    missing: ["container.mustTransport200Miles"],
                    basis: { facility: { federalGeneratorStatus: status } },
                };
            }
            return {
                ...exports.FEDERAL_THRESHOLDS.centralTimeLimit,
                source: federalSource(mustTransport200Miles ? "40 CFR 262.16(c)" : "40 CFR 262.16(b)"),
                determination: "KNOWN",
                basis: {
                    facility: { federalGeneratorStatus: status },
                    container: { mustTransport200Miles },
                },
                value: mustTransport200Miles ? 270 : 180,
            };
        }
    }
}
function isSubclassification(program) {
    return program !== null && program !== "RCRA";
}
/**
 * The clock a subclassification program sets in place of part 262's. Universal waste, used
 * oil, and Subpart P pharmaceuticals are scoped out of part 262, so the
 * satellite caps never sum them either. Each cites the scope-out rule beside
 * its own, because the satellite caps' exemption cites the same source.
 */
const SUBCLASSIFICATION_TIME_LIMITS = {
    UNIVERSAL_WASTE: { ...exports.FEDERAL_THRESHOLDS.universalWasteTimeLimit, determination: "KNOWN" },
    PHARMACEUTICAL: { ...exports.FEDERAL_THRESHOLDS.subpartPTimeLimit, determination: "KNOWN" },
    USED_OIL: { ...exports.FEDERAL_THRESHOLDS.usedOilTimeLimit, determination: "EXEMPT" },
};
/**
 * The satellite caps, which apply simultaneously; crossing one starts its
 * move window. An unknown generator status still gets them, the more
 * protective default; 262.14 lifts them for a VSQG.
 */
function satelliteCaps(situation) {
    const status = situation.facility.federalGeneratorStatus;
    const basis = status === undefined ? {} : { facility: { federalGeneratorStatus: status } };
    if (status === "VSQG") {
        const source = federalSource("40 CFR 262.14");
        return [
            { ...exports.FEDERAL_THRESHOLDS.capNonAcuteVolume, source, determination: "EXEMPT", basis },
            { ...exports.FEDERAL_THRESHOLDS.capAcuteVolume, source, determination: "EXEMPT", basis },
            { ...exports.FEDERAL_THRESHOLDS.capAcuteMass, source, determination: "EXEMPT", basis },
        ];
    }
    return [
        { ...exports.FEDERAL_THRESHOLDS.capNonAcuteVolume, determination: "KNOWN", basis },
        { ...exports.FEDERAL_THRESHOLDS.capAcuteVolume, determination: "KNOWN", basis },
        { ...exports.FEDERAL_THRESHOLDS.capAcuteMass, determination: "KNOWN", basis },
    ];
}
/**
 * The move window a satellite container gets: known once the container counts
 * toward a cap that applies, undetermined while its membership in one is
 * unknown, and none otherwise. Which cap is crossed, and when, is the
 * evaluation's to resolve.
 */
function satelliteMoveWindow(rules, window, caps, container) {
    const missing = [];
    for (const cap of caps) {
        if (cap.determination === "EXEMPT") {
            continue;
        }
        const membership = rules.thresholdIncludes(cap, container);
        if (membership.value === true) {
            return [{ ...window, determination: "KNOWN", basis: {} }];
        }
        for (const fact of membership.missing ?? []) {
            if (!missing.includes(fact)) {
                missing.push(fact);
            }
        }
    }
    if ((0, isNonEmpty_1.isNonEmpty)(missing)) {
        return [{ ...window, determination: "UNDETERMINED", missing, basis: {} }];
    }
    return [];
}
/**
 * The episodic window: 60 days from the event's start for a container
 * attached to an active event. Not for waste a subclassification program governs, which
 * part 262 does not reach. Federal everywhere: a state either adopts this
 * provision or grants nothing.
 */
function episodicWindow(container, program) {
    if (container.attachedToActiveEpisodicEvent === false) {
        return [];
    }
    const missing = [
        ...(container.attachedToActiveEpisodicEvent === undefined
            ? ["container.attachedToActiveEpisodicEvent"]
            : []),
        ...(program.missing ?? []),
    ];
    if ((0, isNonEmpty_1.isNonEmpty)(missing)) {
        return [
            { ...exports.FEDERAL_THRESHOLDS.episodicWindow, determination: "UNDETERMINED", missing, basis: {} },
        ];
    }
    if (program.value !== undefined && isSubclassification(program.value)) {
        return [];
    }
    return [{ ...exports.FEDERAL_THRESHOLDS.episodicWindow, determination: "KNOWN", basis: {} }];
}
/** `rules` decides which cap a container counts toward, so a state's own categories reach the move window. */
function federalContainerThresholds(rules, situation) {
    const program = governingProgram(situation.container);
    const episodic = episodicWindow(situation.container, program);
    if (program.value !== undefined && isSubclassification(program.value)) {
        return [SUBCLASSIFICATION_TIME_LIMITS[program.value], ...episodic];
    }
    switch (situation.area.type) {
        case "CENTRAL":
            return [
                program.missing === undefined
                    ? centralTimeLimit(situation)
                    : undeterminedCentralTimeLimit(program.missing),
                ...episodic,
            ];
        case "SATELLITE":
            // 262.15 imposes no standing satellite clock; the move window is the
            // only time limit, and it reports a missing program itself.
            return [
                ...satelliteMoveWindow(rules, exports.FEDERAL_THRESHOLDS.satelliteToCentralMoveWindow, satelliteCaps(situation), situation.container),
                ...episodic,
            ];
        case undefined:
            return [undeterminedCentralTimeLimit([...(program.missing ?? []), "area.type"]), ...episodic];
    }
}
function federalAreaThresholds(situation) {
    switch (situation.area.type) {
        case "CENTRAL":
            return [];
        case "SATELLITE":
            return satelliteCaps(situation);
        case undefined:
            return [
                {
                    ...exports.FEDERAL_THRESHOLDS.capNonAcuteVolume,
                    determination: "UNDETERMINED",
                    missing: ["area.type"],
                    basis: {},
                },
                {
                    ...exports.FEDERAL_THRESHOLDS.capAcuteVolume,
                    determination: "UNDETERMINED",
                    missing: ["area.type"],
                    basis: {},
                },
                {
                    ...exports.FEDERAL_THRESHOLDS.capAcuteMass,
                    determination: "UNDETERMINED",
                    missing: ["area.type"],
                    basis: {},
                },
            ];
    }
}
/**
 * Federal on-site quantity thresholds (40 CFR 262.14, 262.16): a VSQG is held
 * to 1,000 kg non-acute and 1 kg acute separately, an SQG to one 6,000 kg
 * total, and an LQG to none (it is time-limited instead). Waste a
 * subclassification program governs is outside part 262 and counts toward
 * none of them (261.9, 261.6(a)(4); see 262.13(c)(4), (6), (9)).
 */
function federalOnSiteThresholds(facility) {
    const status = facility.federalGeneratorStatus;
    switch (status) {
        case undefined:
            return [
                {
                    ...exports.FEDERAL_THRESHOLDS.onSiteTotal,
                    source: federalSource("40 CFR 262.14, 262.16"),
                    determination: "UNDETERMINED",
                    missing: ["facility.federalGeneratorStatus"],
                    basis: {},
                    consequences: [{ kind: "COMPLIANCE_NOT_RECORDED" }],
                },
            ];
        case "LQG":
            return [];
        case "SQG":
            return [
                {
                    ...exports.FEDERAL_THRESHOLDS.onSiteTotal,
                    determination: "KNOWN",
                    basis: { facility: { federalGeneratorStatus: status } },
                    value: wasteGeneratorStatusThresholds_1.SQG_ACCUMULATION_THRESHOLD_KG,
                },
            ];
        case "VSQG":
            return [
                {
                    ...exports.FEDERAL_THRESHOLDS.onSiteNonAcute,
                    determination: "KNOWN",
                    basis: { facility: { federalGeneratorStatus: status } },
                    value: wasteGeneratorStatusThresholds_1.VSQG_ACCUMULATION_THRESHOLD_KG,
                },
                {
                    ...exports.FEDERAL_THRESHOLDS.onSiteAcute,
                    determination: "KNOWN",
                    basis: { facility: { federalGeneratorStatus: status } },
                    value: wasteGeneratorStatusThresholds_1.VSQG_ACCUMULATION_THRESHOLD_ACUTE_KG,
                },
            ];
    }
}
function federalDescribeGeneratorStatus(facility) {
    const status = facility.federalGeneratorStatus;
    return status === undefined ? null : exports.FEDERAL_READABLE_GENERATOR_STATUSES[status];
}
/**
 * The federal rules as functions. A state calls these wherever its law
 * matches federal law and writes its own wherever it differs; none of them
 * takes options for a state's variation.
 */
exports.Federal = {
    category,
    governingProgram,
    centralTimeLimit,
    undeterminedCentralTimeLimit,
    subclassificationTimeLimits: SUBCLASSIFICATION_TIME_LIMITS,
    satelliteCaps,
    satelliteMoveWindow,
    areaThresholds: federalAreaThresholds,
    containerThresholds: federalContainerThresholds,
    episodicWindow,
    onSiteThresholds: federalOnSiteThresholds,
    describeGeneratorStatus: federalDescribeGeneratorStatus,
    isSubclassification,
};
/** The FEDERAL registry entry, and the answer for any state without a class of its own. */
class FederalOnlyRules extends _base_1.JurisdictionRules {
    constructor() {
        super(...arguments);
        // Federal law recognizes no state tier, so it refuses none either.
        this.stateStatusDomain = [];
        this.adoptsEpisodicEvents = true;
    }
    facilityThresholds(facility) {
        return federalOnSiteThresholds(facility);
    }
    areaThresholds(situation) {
        return federalAreaThresholds(situation);
    }
    containerThresholds(situation) {
        return federalContainerThresholds(this, situation);
    }
    accumulationCategory(container) {
        return exports.Federal.category(container);
    }
    governingProgram(container) {
        return exports.Federal.governingProgram(container);
    }
    describeGeneratorStatus(facility) {
        return exports.Federal.describeGeneratorStatus(facility);
    }
}
exports.FederalOnlyRules = FederalOnlyRules;
//# sourceMappingURL=_FEDERAL.js.map